import clsx from 'clsx'
import React, {Component} from 'react'
import {v4 as uuidv4} from 'uuid'
import {ExternalViewer} from './external-viewer'
import ViewerLoadingPlaceholder from './viewer-loading-placeholder'
import {
  createCloseViewerMessage,
  createOpenViewerMessage,
  ExternalViewerConfig,
  ExternalViewerKey,
  HandlerAction,
  HandlerActionTable,
  HandlerResponse,
  MessageHandlerContext,
  ViewerMode,
  ViewerDataSource,
  resolveControllerApiUrl,
} from './handler-structures'
import {getObserverToken} from '../../services/observerApi'
import type {DragFileData} from '@/shared/utils/crud-helper/models'
import {removeDragTagPreview, setDragTagPreview} from '@/shared/utils/dragTagPreview'
import {answerDocumentResources} from './document-resources'
import {answerPrintTitle} from './print-title'
import {resolveStorageSource} from './storage-viewer-source'
import {loadViewerManifest} from '../../services/viewerManifest'
import {viewerFeaturesField} from '../../services/viewerFeatures'
import {WebSocketRequest} from './websocket-request.mjs'
import {ViewerExportActions} from './ViewerExportActions'
import {FileTypeIcon} from '@/ui/components/FileIcon'
import {
  canExport,
  createExportMessage,
  resolveExportFormats,
  ViewerExportFormat,
  ViewerExportFormats,
} from './viewerExport'
import {refreshStaleMetadata} from './viewerMetadataRefresh'
import {Id, toast} from 'react-toastify'
import {toastInfiniteConfig} from '@/shared/config/toastConfig'
import styles from './tab-viewer-manager.module.css'

/** Shown when a viewer iframe mounts but never handshakes; see {@code failUnstartedViewer}. */
const VIEWER_DID_NOT_START =
  'This viewer could not be started. It may not be available for this version of the app yet — try reopening the file, or contact support if this persists.'

interface TabViewerManagerProps {
  title?: string
  viewerType?: 'echart' | 'pathway' | 'workflow'
}

interface TabViewerManagerState {
  viewersConfigs: ExternalViewerConfig[]
  selectedTab: number
  /** Export formats each iframe reported it implements, keyed by objectInfo.id. Not persisted. */
  exportCaps: Record<string, Partial<ViewerExportFormats>>
  /**
   * objectInfo.ids of the tabs that have been in front at least once, i.e. the ones whose iframe
   * exists. See {@code syncStartedViewers}. Not persisted: after a reload only the restored selected
   * tab starts.
   */
  startedViewers: Set<string>
}

const OMICSBOX_BACKCHANNEL_WS_SERVER_URL = process.env.REACT_APP_BACKCHANNEL_WS_SERVER_URL

const PING_TIMEOUT = 10000
const WAKEUP_TIMEOUT = 5000
const ENABLE_BACKCHANNEL = process.env.REACT_APP_ENABLE_BACKCHANNEL === 'true'

/**
 * Tabbed viewer manager: displays external viewers as browser-style tabs.
 *
 * Same HandlerActionTable contract as GridViewerManager (openViewer, closeViewer,
 * requestViewer, getApiUrl) so the two are interchangeable via the layout toggle
 * on ResultsPage / WorkflowsPage.
 *
 * Persistence uses the same localStorage keys as GridViewerManager
 * (`grid-viewer-state` / `grid-viewer-state-<viewerType>`) with automatic
 * migration from the grid format.
 */
class TabViewerManager
  extends Component<TabViewerManagerProps, TabViewerManagerState>
  implements HandlerActionTable
{
  initializedViewers: {[key: ExternalViewerKey]: React.RefObject<HTMLIFrameElement>}
  backchannelWS: WebSocketRequest
  id: string

  // Keyed by objectInfo.id (the export message's reqId) — lets exportComplete resolve the right
  // "loading" toast once the iframe's fetch actually finishes, which can take a while for a
  // server-rendered PDF/TSV.
  private exportToasts: Record<string, Id> = {}

  // objectInfo.ids a metadata refresh has been started for, so a restored tab is re-requested at
  // most once per session — see refreshRestoredMetadata.
  private metadataRefreshAttempted = new Set<string>()

  _pingInterval?: ReturnType<typeof setInterval>
  _lastWakeupCheck: number
  _wakeupInterval?: ReturnType<typeof setInterval>

  constructor(props: TabViewerManagerProps) {
    super(props)

    const persistedState = this.loadViewerState()

    this.state = {
      viewersConfigs: persistedState.viewersConfigs || [],
      selectedTab: persistedState.selectedTab !== undefined ? persistedState.selectedTab : -1,
      exportCaps: {},
      startedViewers: new Set(),
    }

    this.initializedViewers = {}
    this.id = uuidv4()

    if (ENABLE_BACKCHANNEL) {
      this.backchannelWS = new WebSocketRequest({
        url: OMICSBOX_BACKCHANNEL_WS_SERVER_URL,
        serverCallback: this.wsMessageHandler.bind(this),
      })
    } else {
      this.backchannelWS = new WebSocketRequest({websocket: {addEventListener: () => {}}})
    }

    this._lastWakeupCheck = Date.now()
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessageEvent)
    // Warm the viewer manifest before any iframe mounts, so a tab restored from localStorage resolves
    // its version from the manifest on the first paint rather than re-pointing its iframe afterwards.
    void loadViewerManifest()
    this.syncStartedViewers()

    if (ENABLE_BACKCHANNEL) {
      this._wakeupInterval = setInterval(this.wakeupCheck.bind(this), WAKEUP_TIMEOUT)
      this._pingInterval = setInterval(this.pingBackchannel.bind(this), PING_TIMEOUT)

      window.addEventListener('online', this.registerBackchannel.bind(this))

      this.registerBackchannel()
    }
  }

  componentDidUpdate(_prevProps: TabViewerManagerProps, prevState: TabViewerManagerState) {
    if (
      prevState.selectedTab !== this.state.selectedTab ||
      prevState.viewersConfigs !== this.state.viewersConfigs
    ) {
      this.syncStartedViewers()
    }
  }

  /**
   * Record that the tab currently in front has been shown, and forget tabs that are gone.
   *
   * A tab's iframe is only created once its panel has been in front (see {@code isTabStarted}), so a
   * viewer nobody has looked at never runs. That matters most straight after a reload, which brings
   * back every tab of the previous session at once: starting all of them costs a bundle load and a
   * data fetch each, and a viewer that sizes itself from its viewport as it loads can fail outright
   * in a panel that is not in front. Krona is the clearest case — it derives its chart radius that
   * way and, coming up in a background tab, threw
   * `IndexSizeError: The radius provided (-182.933) is negative` on every frame of its redraw loop
   * until that tab was selected.
   *
   * Started viewers stay mounted, so moving between tabs never reloads one or loses its state.
   */
  private syncStartedViewers = () => {
    const {viewersConfigs, selectedTab, startedViewers} = this.state
    const liveIds = new Set(viewersConfigs.map((c) => c.objectInfo.id))
    const active = selectedTab >= 0 ? viewersConfigs[selectedTab] : undefined
    const activeId = active && !active.loading && !active.error ? active.objectInfo.id : undefined

    const next = new Set([...startedViewers].filter((id) => liveIds.has(id)))
    if (activeId) next.add(activeId)

    this.refreshRestoredMetadata(next)

    if (next.size === startedViewers.size && [...next].every((id) => startedViewers.has(id))) {
      return
    }
    this.setState({startedViewers: next})
  }

  /**
   * Re-request `/metadata` for started tabs whose persisted metadata predates what the frontend
   * now reads (see {@code VIEWER_METADATA_SCHEMA} in viewerMetadataRefresh).
   *
   * Nothing else refreshes it: `getFileMetadata` is called by the open flow only, and the config
   * this manager persists — `controllerInfo` included — is restored verbatim. So a tab kept open
   * across reloads answers with the capabilities of the day it was opened forever, which is how a
   * table opened before the OBserver advertised `SAVE_AS_CSV_ALLOWED` ends up with no export icons
   * while the same file opened today has them.
   *
   * Only started tabs are refreshed, and each at most once per session, so restoring a session of
   * many tabs costs one request per tab the user actually looks at.
   */
  private refreshRestoredMetadata = (startedIds: ReadonlySet<string>) => {
    refreshStaleMetadata({
      configs: this.state.viewersConfigs.filter((config) =>
        startedIds.has(String(config.objectInfo.id))
      ),
      attempted: this.metadataRefreshAttempted,
      onRefreshed: this.applyRefreshedMetadata,
    })
  }

  /**
   * Swap a refreshed config in place, keeping its tab position, and persist the fresh answer so the
   * next reload starts from it. Tabs closed while the request was in flight are not resurrected —
   * only an entry that is still there is replaced.
   */
  private applyRefreshedMetadata = (refreshed: ExternalViewerConfig) => {
    this.setState(
      (prev) => ({
        viewersConfigs: prev.viewersConfigs.map((config) =>
          String(config.objectInfo.id) === String(refreshed.objectInfo.id) ? refreshed : config
        ),
      }),
      this.saveViewerState
    )
  }

  /** Whether this tab's viewer should be mounted: it is in front, or has been. */
  private isTabStarted = (item: ExternalViewerConfig, index: number): boolean =>
    this.state.selectedTab === index || this.state.startedViewers.has(item.objectInfo.id)

  /**
   * Let a tab be dragged onto the AI chat composer, where it becomes a "Results: <name>" chip. Uses
   * the same payload as a Files-table row. Tabs still loading, failed, or restored without a source
   * path have nothing to attach and are not draggable.
   */
  private handleTabDragStart = (e: React.DragEvent, item: ExternalViewerConfig) => {
    const path = item.sourcePath
    if (!path || item.loading || item.error) {
      e.preventDefault()
      return
    }
    const dragData: DragFileData = {
      type: 'file',
      id: path,
      name: item.objectInfo?.name ?? path,
      isDir: false,
      source: 'result',
      // From a folder someone else shares: the chat refuses it for now and says why (P1-F6).
      ...(item.share && item.share.access !== 'owner' ? {shareId: item.share.id} : {}),
    }
    e.dataTransfer.setData('application/json', JSON.stringify(dragData))
    e.dataTransfer.effectAllowed = 'copy'
    // Drag the "Results: <name>" chip it becomes in the chat, not a ghost of the tab.
    removeDragTagPreview(this._dragPreview)
    this._dragPreview = setDragTagPreview(e, `Results: ${dragData.name}`, 'result')
  }

  private _dragPreview: HTMLElement | null = null

  private handleTabDragEnd = () => {
    removeDragTagPreview(this._dragPreview)
    this._dragPreview = null
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessageEvent)

    if (ENABLE_BACKCHANNEL) {
      window.removeEventListener('online', this.registerBackchannel)

      if (this._pingInterval) {
        clearInterval(this._pingInterval)
      }
      if (this._wakeupInterval) {
        clearInterval(this._wakeupInterval)
      }

      this.unregisterBackchannel()
    }
  }

  // ── Persistence ──────────────────────────────────────────────────────

  private get storageKey(): string {
    return this.props.viewerType
      ? `grid-viewer-state-${this.props.viewerType}`
      : 'grid-viewer-state'
  }

  private saveViewerState = () => {
    // Never persist optimistic loading placeholders (B1): they are transient and would otherwise be
    // restored as dead "loading" tabs on reload with no request behind them. Also clear the
    // transient "initializing" overlay flag so a restored tab shows its viewer, not a stuck spinner.
    const persistedConfigs = this.state.viewersConfigs
      .filter((c) => !c.loading && !c.error)
      .map((c) => (c.initializing ? {...c, initializing: false} : c))
    const selectedTab = Math.min(this.state.selectedTab, persistedConfigs.length - 1)
    const stateToSave = {
      viewersConfigs: persistedConfigs,
      selectedTab,
    }
    localStorage.setItem(this.storageKey, JSON.stringify(stateToSave))
  }

  private loadViewerState = (): Partial<TabViewerManagerState> => {
    try {
      const savedState = localStorage.getItem(this.storageKey)

      if (savedState) {
        const parsed = JSON.parse(savedState)

        // Migration: GridViewerManager stored { viewers: GridViewerItem[], layout }
        if (parsed.viewers && Array.isArray(parsed.viewers)) {
          const migratedConfigs: ExternalViewerConfig[] = parsed.viewers.map(
            (item: {config: ExternalViewerConfig}) => item.config
          )
          const migrated = {
            viewersConfigs: migratedConfigs,
            selectedTab: migratedConfigs.length > 0 ? 0 : -1,
          }
          localStorage.setItem(this.storageKey, JSON.stringify(migrated))
          return migrated
        }

        return {
          viewersConfigs: parsed.viewersConfigs || [],
          selectedTab: parsed.selectedTab !== undefined ? parsed.selectedTab : -1,
        }
      }

      // Fallback: migrate from legacy viewer-state keys
      const oldKey = this.props.viewerType
        ? `viewer-state-${this.props.viewerType}`
        : 'viewer-state'
      const oldSaved = localStorage.getItem(oldKey)

      if (oldSaved) {
        const oldParsed = JSON.parse(oldSaved)
        const migrated = {
          viewersConfigs: oldParsed.viewersConfigs || [],
          selectedTab: oldParsed.selectedTab !== undefined ? oldParsed.selectedTab : -1,
        }
        localStorage.setItem(this.storageKey, JSON.stringify(migrated))
        localStorage.removeItem(oldKey)
        return migrated
      }
    } catch {
      // corrupt localStorage — start fresh
    }
    return {viewersConfigs: [], selectedTab: -1}
  }

  private clearViewerState = () => {
    localStorage.removeItem(this.storageKey)
  }

  private clearAllViewers = () => {
    this.setState({viewersConfigs: [], selectedTab: -1}, this.clearViewerState)
  }

  // ── Backchannel WebSocket ────────────────────────────────────────────

  wsMessageHandler = (msg: string | JSON) => {
    const msgData = typeof msg === 'string' ? JSON.parse(msg) : msg
    const msgAction = (msgData as Record<string, unknown>).data as Record<string, unknown>
    const action = (msgAction as Record<string, unknown>).action as string

    const handlers: Record<string, (d: Record<string, unknown>) => void> = {
      changeViewerStatus: (d) => {
        const vs = d.data as {data: {viewerId: string; mode: ViewerMode}}
        this.changeViewerStatus(vs.data.viewerId, vs.data.mode)
        this.forceUpdate()

        this.backchannelWS.trySend({
          reqid: d.reqid,
          data: {
            action: 'changeViewerStatus',
            changed: true,
            viewerId: (vs as unknown as {viewerId: string}).viewerId,
          },
        })
      },
    }

    if (action in handlers) {
      handlers[action](msgData as Record<string, unknown>)
    }
  }

  private changeViewerStatus(viewerId: string, status: ViewerMode) {
    const config = this.state.viewersConfigs.find((c) => c.objectInfo.id === viewerId)
    if (config) config.mode = status
    this.forceUpdate()
  }

  private registerBackchannel = () => {
    this.backchannelWS.sendJson(
      {action: 'register', viewer: {id: this.id, viewers: this.state.viewersConfigs}},
      (data: {modeChanges?: Record<string, ViewerMode>}) => {
        Object.entries(data.modeChanges ?? {}).forEach(([id, mode]) =>
          this.changeViewerStatus(id, mode)
        )
      }
    )
  }

  private unregisterBackchannel = () => {
    this.backchannelWS.sendJson({action: 'unregister', viewer: {id: this.id}})
  }

  private wakeupCheck = () => {
    const now = Date.now()
    if (now > this._lastWakeupCheck + WAKEUP_TIMEOUT + 5000) this.registerBackchannel()
    this._lastWakeupCheck = now
  }

  private pingBackchannel = () => {
    this.backchannelWS.sendJson(
      {action: 'ping', viewer: {id: this.id}},
      (data: {success?: boolean}) => {
        if (data.success === false) this.registerBackchannel()
      }
    )
  }

  private updateViewers = () => {
    this.backchannelWS.sendJson({
      action: 'updateViewers',
      viewer: {id: this.id, viewers: this.state.viewersConfigs},
    })
  }

  // ── Viewer lookup helpers ────────────────────────────────────────────

  private getPropsById = (id: ExternalViewerKey): ExternalViewerConfig | undefined =>
    this.state.viewersConfigs.find((c) => String(c.objectInfo.id) === String(id))

  private getPropsByRef = (ref: MessageEventSource): ExternalViewerConfig | undefined => {
    try {
      if ('frameElement' in ref) {
        const iframe = (ref as Window).frameElement as HTMLIFrameElement
        if (iframe) {
          const key = Object.keys(this.initializedViewers).find(
            (k) => this.initializedViewers[k].current === iframe
          )
          if (key) return this.getPropsById(key)
        }
      }
    } catch {
      // cross-origin
    }

    const key = Object.keys(this.initializedViewers).find(
      (k) => this.initializedViewers[k].current?.contentWindow === ref
    )
    return key ? this.getPropsById(key) : undefined
  }

  // ── HandlerActionTable implementation ────────────────────────────────

  // Clear the "initializing" overlay for a viewer, revealing its iframe (B1/#3). Idempotent.
  private clearInitializing = (id: string) => {
    if (!this.state.viewersConfigs.some((c) => c.objectInfo.id === id && c.initializing)) {
      return
    }
    this.setState(
      {
        viewersConfigs: this.state.viewersConfigs.map((c) =>
          c.objectInfo.id === id ? {...c, initializing: false} : c
        ),
      },
      this.saveViewerState
    )
  }

  /**
   * A viewer that never asked for its API URL never started; show that as an error rather than
   * revealing the iframe. Idempotent, and a no-op once the viewer has handshaken.
   *
   * The error message matters because the usual cause is invisible: an iframe pointed at a viewer
   * version that is not published is answered by the CDN with the OmicsBox app's own index.html
   * (HTTP 200), so the frame loads successfully and would otherwise show the entire app nested
   * inside the tab. Because error configs are excluded from persistence, this also keeps a broken
   * tab from surviving a reload.
   */
  private failUnstartedViewer = (id: string) => {
    if (!this.state.viewersConfigs.some((c) => c.objectInfo.id === id && c.initializing)) {
      return
    }
    this.setState(
      {
        viewersConfigs: this.state.viewersConfigs.map((c) =>
          c.objectInfo.id === id
            ? {...c, initializing: false, error: VIEWER_DID_NOT_START}
            : c
        ),
      },
      this.saveViewerState
    )
  }

  getApiUrl = async (e: MessageEvent): Promise<void> => {
    if (!e.source) return
    const props = this.getPropsByRef(e.source)
    if (props) {
      // The viewer asking for its API URL means it has mounted and is ready to load data — hide the
      // loading overlay now so the swap from placeholder to viewer has no blank flash.
      this.clearInitializing(props.objectInfo.id)
      const iframeWindow = e.source as Window
      // Storage-backed viewer: reads the file directly from S3, so hand it a presigned URL + the
      // OBserver-resolved format instead of a controller endpoint. Generic — driven by dataSource,
      // not by the viewer's name.
      if (props.controllerInfo.dataSource === ViewerDataSource.STORAGE && props.objectInfo.objectUri) {
        const source = await resolveStorageSource(props.objectInfo.objectUri, props.objectInfo.type, props.share)
        iframeWindow.postMessage({reqId: props.objectInfo.id, action: 'getApiUrl', ...source, ...viewerFeaturesField()}, '*')
        return
      }
      // Hand the iframe the ID token so its /controller requests to the OBserver authenticate.
      const token = await getObserverToken()
      iframeWindow.postMessage(
        {
          reqId: props.objectInfo.id,
          value: resolveControllerApiUrl(props.controllerInfo),
          action: 'getApiUrl',
          token,
          // What this deployment wants the viewer to offer; omitted entirely when it declares
          // nothing. See services/viewerFeatures.ts — this is why develop and release differ
          // while running the identical viewer bundle.
          ...viewerFeaturesField(),
        },
        '*'
      )
      // The viewer is listening now, so ask what it can export. Until it answers, no export icons
      // are shown — better than offering a format the viewer has no handler for.
      iframeWindow.postMessage(
        {reqId: props.objectInfo.id, action: 'getExportCapabilities'},
        '*'
      )
    }
  }

  // A storage-backed document asks for the images it references to be signed (see document-resources.ts).
  resolveDocumentResources = (e: MessageEvent): Promise<void> =>
    answerDocumentResources(e, e.source ? this.getPropsByRef(e.source) : undefined)

  // A viewer about to print asks for the page title that names the saved PDF (see print-title.ts).
  setPrintTitle = (e: MessageEvent): void =>
    answerPrintTitle(e, e.source ? this.getPropsByRef(e.source) : undefined)

  // An iframe's OBserver call came back 401 (baked-in token expired). Hand it a fresh token
  // (Auth.currentSession() auto-refreshes) so it can retry without being reopened.
  refreshToken = async (e: MessageEvent): Promise<void> => {
    if (!e.source) return
    const token = await getObserverToken()
    const iframeWindow = e.source as Window
    iframeWindow.postMessage({action: 'refreshToken', token}, '*')
  }

  openViewer = (e: MessageEvent) => {
    const viewerConfig = e.data.value as ExternalViewerConfig

    const mapViewerType = (type: string): string => {
      const typeMap: Record<string, string> = {
        Chart: 'echart',
        chart: 'echart',
        echart: 'echart',
        Echart: 'echart',
        Pathway: 'pathway',
        pathway: 'pathway',
        PathwaysAnalysisObject: 'pathway',
        pathwaysanalysisobject: 'pathway',
        Workflow: 'workflow',
        workflow: 'workflow',
      }
      return typeMap[type] || type.toLowerCase()
    }

    // Loading placeholders (B1) carry a synthetic 'loading' type and must not be filtered out by a
    // grid manager's viewerType; the real config that replaces them is filtered normally.
    if (
      !viewerConfig.loading &&
      this.props.viewerType &&
      mapViewerType(viewerConfig.objectInfo.type) !== this.props.viewerType
    ) {
      return
    }

    // Real config resolving an optimistic placeholder: replace the placeholder in place (same tab
    // slot) so the loading spinner becomes the viewer without a flash or a duplicate tab. If a real
    // tab for the same object is already open elsewhere, drop the placeholder and focus that one.
    if (!viewerConfig.loading && viewerConfig.correlationId) {
      const placeholderIndex = this.state.viewersConfigs.findIndex(
        (c) => c.loading && c.correlationId === viewerConfig.correlationId
      )
      if (placeholderIndex >= 0) {
        const hasDuplicate = this.state.viewersConfigs.some(
          (c, i) => i !== placeholderIndex && !c.loading && c.objectInfo.id === viewerConfig.objectInfo.id
        )
        if (hasDuplicate) {
          const withoutPlaceholder = this.state.viewersConfigs.filter((_, i) => i !== placeholderIndex)
          const focusIndex = withoutPlaceholder.findIndex(
            (c) => !c.loading && c.objectInfo.id === viewerConfig.objectInfo.id
          )
          this.setState(
            {viewersConfigs: withoutPlaceholder, selectedTab: Math.max(0, focusIndex)},
            () => {
              this.saveViewerState()
              this.updateViewers()
            }
          )
          return
        }
        const replaced = [...this.state.viewersConfigs]
        replaced[placeholderIndex] = viewerConfig
        this.setState({viewersConfigs: replaced, selectedTab: placeholderIndex}, () => {
          this.saveViewerState()
          this.updateViewers()
        })
        // Safety net: a viewer that never asks for its API URL never started. Surfacing that as an
        // error matters more than it looks — a viewer URL that does not exist is answered by the CDN
        // with the OmicsBox app's own index.html (HTTP 200, SPA fallback), not a 404, so the iframe
        // "loads" fine and merely revealing it would render the whole app inside a Results tab with
        // no hint of what went wrong.
        if (viewerConfig.initializing) {
          setTimeout(() => this.failUnstartedViewer(viewerConfig.objectInfo.id), 15000)
        }
        return
      }
    }

    const existingIndex = this.state.viewersConfigs.findIndex(
      (c) => !c.loading && c.objectInfo.id === viewerConfig.objectInfo.id
    )

    if (existingIndex >= 0) {
      this.setState({selectedTab: existingIndex}, this.saveViewerState)
      return
    }

    const newConfigs = [...this.state.viewersConfigs, viewerConfig]
    this.setState(
      {viewersConfigs: newConfigs, selectedTab: newConfigs.length - 1},
      () => {
        this.saveViewerState()
        this.updateViewers()
      }
    )
  }

  requestViewer = (e: MessageEvent) => {
    const viewerConfig = e.data.value as ExternalViewerConfig

    // Skip if already open — UNLESS this config resolves an optimistic placeholder (correlationId):
    // then it must still reach openViewer so the leftover placeholder gets reconciled/removed.
    if (
      !viewerConfig.correlationId &&
      this.state.viewersConfigs.some(
        (c) => !c.loading && String(c.objectInfo.id) === String(viewerConfig.objectInfo.id)
      )
    ) {
      return
    }

    if (ENABLE_BACKCHANNEL) {
      this.backchannelWS.sendJson(
        {action: 'isViewerLocked', viewer: {id: this.id, config: viewerConfig}},
        (response: {locked: boolean; config: ExternalViewerConfig}) => {
          response.config.mode = response.locked ? ViewerMode.READ : ViewerMode.WRITE
          window.postMessage(createOpenViewerMessage(response.config), '/')
        }
      )
    } else {
      window.postMessage(createOpenViewerMessage(viewerConfig), '/')
    }
  }

  closeViewer = (e: MessageEvent) => {
    const viewerConfig = e.data.value as ExternalViewerConfig

    const closingIndex = this.state.viewersConfigs.findIndex(
      (c) => c.objectInfo.id === viewerConfig.objectInfo.id
    )
    if (closingIndex < 0) return

    const newConfigs = this.state.viewersConfigs.filter(
      (c) => c.objectInfo.id !== viewerConfig.objectInfo.id
    )

    let newTab = this.state.selectedTab
    if (newConfigs.length === 0) {
      newTab = -1
    } else if (closingIndex <= this.state.selectedTab) {
      newTab = Math.max(0, this.state.selectedTab - 1)
    }

    this.setState({viewersConfigs: newConfigs, selectedTab: newTab}, () => {
      this.updateViewers()
      this.saveViewerState()
    })
  }

  handleMessageEvent = (e: MessageEvent) => {
    // Anything can post to window — extensions post strings, dev tooling posts nulls — and reading
    // .action off one of those throws inside a global listener.
    if (typeof e.data !== 'object' || e.data === null) return

    const action = e.data.action as HandlerAction
    if (action in this) this[action](e)
  }

  // ── MessageHandlerContext provider ───────────────────────────────────

  registerViewer = (key: string, iframe: React.RefObject<HTMLIFrameElement>) => {
    this.initializedViewers[key] = iframe
  }

  unregisterViewer = (key: string) => {
    delete this.initializedViewers[key]
  }

  sendMessageToViewer = (iframe: React.RefObject<HTMLIFrameElement>, response: HandlerResponse) => {
    iframe.current?.contentWindow?.postMessage(response, '*')
  }

  /**
   * Ask the active tab's iframe to export (getPNG / getPDF / getCSV). The iframe produces the
   * payload and its ConnectionManager decides where it goes: a PNG is downloaded straight from the
   * browser, PDF/TSV are rendered by the OBserver and downloaded from its response.
   */
  handleExport = (format: ViewerExportFormat) => {
    const config = this.state.viewersConfigs[this.state.selectedTab]
    const iframe = config && this.initializedViewers[config.objectInfo.id]
    if (!canExport(config, iframe)) {
      return
    }
    this.exportToasts[config.objectInfo.id] = toast.loading(
      `Exporting ${format.toUpperCase()}…`,
      toastInfiniteConfig
    )
    this.sendMessageToViewer(
      iframe,
      createExportMessage(format, config.objectInfo.id, config.objectInfo.name)
    )
  }

  /**
   * The iframe's fetch for a server-rendered export (PDF/TSV) finished — resolve the "loading"
   * toast handleExport started. A client-only download (PNG, or CSV once the table is fully
   * loaded) reaches here almost immediately, which is fine: the toast just flashes to success.
   */
  exportComplete = (e: MessageEvent) => {
    const {reqId, kind, success} = e.data as {reqId?: string; kind?: string; success?: boolean}
    if (!reqId) return
    const toastId = this.exportToasts[reqId]
    if (toastId === undefined) return
    delete this.exportToasts[reqId]
    toast.update(toastId, {
      render: success ? `${(kind ?? 'Export').toUpperCase()} export ready` : 'Export failed',
      type: success ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
      isLoading: false,
      autoClose: 3000,
    })
  }

  /**
   * An iframe reported which export formats it implements (reply to getExportCapabilities). Kept
   * separate from the controller's workbenchProperties: a controller may allow a format the viewer
   * has no handler for, and only the intersection gets an icon.
   */
  exportCapabilities = (e: MessageEvent) => {
    if (!e.source) return
    const props = this.getPropsByRef(e.source)
    if (!props) return
    const value = e.data.value as Partial<ViewerExportFormats> | undefined
    this.setState((prev) => ({
      exportCaps: {...prev.exportCaps, [props.objectInfo.id]: value ?? {}},
    }))
  }

  /** Formats to show for a viewer: controller-allowed ∩ iframe-implemented. */
  private exportFormatsFor(config: ExternalViewerConfig): ViewerExportFormats {
    return resolveExportFormats(
      config.controllerInfo?.workbenchProperties,
      this.state.exportCaps[config.objectInfo.id]
    )
  }

  /** Export strip in the tab header's trailing slot — acts on the active tab, like the desktop. */
  private renderExportActions() {
    const active = this.state.viewersConfigs[this.state.selectedTab]
    if (!active || active.loading || active.error) {
      return null
    }
    return (
      <ViewerExportActions
        className={styles.tabExportActions}
        formats={this.exportFormatsFor(active)}
        onExport={this.handleExport}
      />
    )
  }

  handleRequestMode = (key: string, mode: ViewerMode) => {
    this.backchannelWS.sendJson(
      {action: 'requestOwnership', requestedViewerId: key, requestedMode: mode, viewer: {id: this.id}},
      (message: {mode?: ViewerMode}) => {
        const cfg = this.state.viewersConfigs.find(
          (c) => String(c.objectInfo.id) === String(key)
        )
        if (cfg) cfg.mode = message.mode ?? ViewerMode.READ
        this.forceUpdate()
        this.updateViewers()
      }
    )
  }

  getMessageHandlerProvider() {
    return {
      registerViewer: this.registerViewer,
      unregisterViewer: this.unregisterViewer,
      sendMessage: this.sendMessageToViewer,
      handleAction: this.handleMessageEvent,
      requestMode: this.handleRequestMode,
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  /**
   * The file-type icon of a tab, the same one the file manager shows for its source file. Only for a
   * tab opened from a file: a placeholder or error tab has no type yet, and a viewer opened by other
   * means has no file to take one from.
   */
  renderTabIcon(item: ExternalViewerConfig) {
    if (item.loading || item.error || (!item.sourcePath && !item.tags)) return null
    return (
      <span className={styles.tabIcon} data-testid='tab-file-icon'>
        <FileTypeIcon name={item.sourcePath ?? item.objectInfo.name} tags={item.tags} />
      </span>
    )
  }

  render() {
    if (this.state.viewersConfigs.length === 0) return null

    return (
      <MessageHandlerContext.Provider value={this.getMessageHandlerProvider()}>
        <div className={styles.tabbedViewerContainer}>
          {/* Tab Header */}
          <div className={styles.tabHeader}>
            <ul
              className={clsx('nav nav-tabs nav-line-tabs', styles.tabList)}
              role='tablist'
            >
              {this.state.viewersConfigs.map((item, index) => (
                <li className='nav-item' key={'li_' + item.objectInfo.id}>
                  <div
                    className={clsx('nav-link cursor-pointer', styles.viewerTab, {
                      active: index === this.state.selectedTab,
                      [styles.activeTab]: index === this.state.selectedTab,
                    })}
                    data-bs-toggle='tab'
                    role='tab'
                    draggable={Boolean(item.sourcePath && !item.loading && !item.error)}
                    onDragStart={(e) => this.handleTabDragStart(e, item)}
                    onDragEnd={this.handleTabDragEnd}
                    title={item.sourcePath ? `${item.objectInfo.name} — drag to the AI chat to attach it` : undefined}
                    onClick={() => {
                      if (this.state.selectedTab !== index) {
                        this.setState({selectedTab: index}, this.saveViewerState)
                      }
                    }}
                  >
                    {this.renderTabIcon(item)}
                    <span className={styles.tabLabel}>{item.objectInfo.name}</span>
                    <span
                      role='button'
                      tabIndex={0}
                      className={styles.tabCloseBtn}
                      onClick={(e) => {
                        e.stopPropagation()
                        window.postMessage(createCloseViewerMessage(item))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation()
                          window.postMessage(createCloseViewerMessage(item))
                        }
                      }}
                      title='Close tab'
                    >
                      &times;
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            {this.renderExportActions()}
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>
            {this.state.viewersConfigs.map((item, index) => (
              <div
                className={clsx(styles.tabPanel, {
                  [styles.activeTabPanel]: this.state.selectedTab === index,
                  [styles.inactiveTabPanel]: this.state.selectedTab !== index,
                })}
                role='tabpanel'
                key={'tab_' + item.objectInfo.id}
              >
                {item.error ? (
                  <ViewerLoadingPlaceholder
                    key={item.objectInfo.id}
                    error={item.error}
                    errorKind={item.errorKind}
                    name={item.objectInfo.name}
                  />
                ) : item.loading ? (
                  <ViewerLoadingPlaceholder
                    key={item.objectInfo.id}
                    message={item.loadingMessage}
                    name={item.objectInfo.name}
                  />
                ) : !this.isTabStarted(item, index) ? null : (
                  <div
                    style={{
                      position: 'relative',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      width: '100%',
                    }}
                  >
                    <ExternalViewer key={item.objectInfo.id} {...item} />
                    {item.initializing && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: 2,
                          background: 'var(--ob-body-bg, var(--bs-body-bg, #ffffff))',
                        }}
                      >
                        <ViewerLoadingPlaceholder
                          message={item.loadingMessage}
                          name={item.objectInfo.name}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </MessageHandlerContext.Provider>
    )
  }
}

export default TabViewerManager
